/**
 * HoBoqTab - Bill of Quantities tab for active project detail.
 * Matched exactly to extracted_assets_2: bf832ac1-9d48-45db-a651-9a4805854e40
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SkeletonRows } from "@/components/ui/skeleton";
import { StudioButton } from "@/features/studio/theme";
import { useProjectBOQs } from "../../../hooks/use-boq";
import { useProject } from "../../../hooks/use-projects";
import {
	type BOQDetails,
	boqEntryName,
	type GetProductAlternativesResult,
	getBOQById,
	getProductAlternatives,
	homeownerApproveBoqEntry,
	homeownerBulkApproveBoqEntries,
} from "../../../services/boq/boq-api.service";
import {
	formatGstAmount,
	formatGstIncluded,
	formatGstRate,
	GST_INCLUDED_HINT,
	summariseGstIncluded,
} from "../../../utils/boq-gst";
import { openBoqPrintView } from "../../boq/open-boq-print-view";
import { RequestChangesDialog } from "../../boq/request-changes-dialog";
import {
	Btn,
	Eyebrow,
	FloatPanel,
	formatPaiseINR,
	Icn,
	ItalicAccent,
	Progress,
	secondaryBtnStyle,
} from "../design-primitives";

interface BoqCategory {
	id: string;
	name: string;
	items: BoqItem[];
}

interface BoqItem {
	id: string;
	boqSectionId: string;
	desc: string;
	room: string;
	qty: number;
	unit: string;
	rate: number;
	amount: number;
	gstRateBp: number | null;
	gstAmount: number | null;
	approved: boolean;
	approvedByHomeowner: boolean;
	/** Null for a custom line, which has no catalogue alternatives. */
	catalogProductId: string | null;
}

function transformBOQToCategories(
	boqDetails: BOQDetails | null,
): BoqCategory[] {
	if (!boqDetails) return [];

	return boqDetails.sections.map((section) => ({
		id: section.id,
		name: section.sectionName,
		items: (section.entries || []).map((entry) => ({
			id: entry.id,
			boqSectionId: entry.boqSectionId,
			desc: entry.description || boqEntryName(entry),
			room: entry.roomCustomName || entry.roomName || "Not assigned",
			qty: entry.quantity,
			unit: entry.uom || "piece",
			rate: entry.unitPrice,
			amount: entry.lineTotal,
			gstRateBp: entry.gstRateBp ?? null,
			gstAmount: entry.gstAmount ?? null,
			approved: entry.approvedByHomeowner,
			approvedByHomeowner: entry.approvedByHomeowner,
			catalogProductId: entry.catalogProductId,
		})),
	}));
}

const boqHead: React.CSSProperties = {
	fontFamily: "var(--font-mono)",
	fontSize: 9,
	fontWeight: 600,
	letterSpacing: "0.12em",
	color: "var(--color-dashboard-muted)",
	textTransform: "uppercase",
};

interface HoBoqTabProps {
	projectId: string;
	accent?: string;
	onAskDesigner?: (userId: string) => void;
}

export function HoBoqTab({
	projectId,
	accent = "var(--color-dashboard-accent)",
	onAskDesigner,
}: HoBoqTabProps) {
	const { t } = useTranslation();
	const {
		data: boqs,
		isLoading: boqsLoading,
		refetch: refetchBoqs,
	} = useProjectBOQs(projectId);
	const [showRequestChanges, setShowRequestChanges] = React.useState(false);
	const { data: project } = useProject(projectId);
	const [boqDetails, setBoqDetails] = React.useState<BOQDetails | null>(null);
	const [loadingDetails, setLoadingDetails] = React.useState(false);
	const [selectedBoqId, setSelectedBoqId] = React.useState<string | null>(null);
	const [view, setView] = React.useState<"list" | "detail">("list");

	const handleSelectBoq = (boqId: string) => {
		setSelectedBoqId(boqId);
		setView("detail");
	};

	const handleBackToList = () => {
		setSelectedBoqId(null);
		setView("list");
		setBoqDetails(null);
		setCats([]);
	};

	React.useEffect(() => {
		if (!selectedBoqId) return;

		setLoadingDetails(true);
		getBOQById(selectedBoqId)
			.then((result) => {
				// The API client's own toast reports the failure.
				if (result.error) {
					setBoqDetails(null);
					return;
				}
				setBoqDetails(result.data);
				if (result.data) {
					setCats(transformBOQToCategories(result.data));
				}
			})
			.catch(() => {
				setBoqDetails(null);
			})
			.finally(() => setLoadingDetails(false));
	}, [selectedBoqId]);

	const [cats, setCats] = React.useState<BoqCategory[]>([]);
	const [boqDetail, setBoqDetail] = React.useState<{
		ci: number;
		ii: number;
	} | null>(null);

	const [approveConfirm, setApproveConfirm] = React.useState<{
		show: boolean;
		ci: number;
		ii: number;
		item: BoqItem | null;
	}>({ show: false, ci: 0, ii: 0, item: null });

	const [approveAllConfirm, setApproveAllConfirm] = React.useState(false);
	const [approvingId, setApprovingId] = React.useState<string | null>(null);
	const [approvingAll, setApprovingAll] = React.useState(false);

	const detailItem = boqDetail ? cats[boqDetail.ci]?.items[boqDetail.ii] : null;

	const [alternatives, setAlternatives] =
		React.useState<GetProductAlternativesResult | null>(null);
	const [loadingAlternatives, setLoadingAlternatives] = React.useState(false);

	React.useEffect(() => {
		if (!detailItem) {
			setAlternatives(null);
			return;
		}

		if (!detailItem.catalogProductId) {
			setAlternatives(null);
			return;
		}

		setLoadingAlternatives(true);
		getProductAlternatives(detailItem.catalogProductId)
			.then((result) => {
				if (result.error) {
					toast.error(result.error);
					setAlternatives(null);
					return;
				}
				setAlternatives(result.data);
			})
			.catch(() => {
				setAlternatives(null);
			})
			.finally(() => setLoadingAlternatives(false));
	}, [detailItem]);

	// An approval can move the BOQ to Approved, so its status is read again for
	// the list's card and this view's controls. Only the latest read for the BOQ
	// still open may land: an earlier one finishing late, or one for a BOQ the
	// homeowner has since left, would put back a stale status.
	const latestRefresh = React.useRef(0);
	const openBoqId = React.useRef(selectedBoqId);
	openBoqId.current = selectedBoqId;
	const refreshStatus = async () => {
		void refetchBoqs();
		const boqId = selectedBoqId;
		if (!boqId) return;
		const refresh = ++latestRefresh.current;
		const result = await getBOQById(boqId);
		if (refresh !== latestRefresh.current || openBoqId.current !== boqId) {
			return;
		}
		if (result.data) setBoqDetails(result.data);
	};

	const toggleBoq = (ci: number, ii: number, item: BoqItem) => {
		if (item.approvedByHomeowner) return;
		setApproveConfirm({ show: true, ci, ii, item });
	};

	const handleApproveEntry = async () => {
		if (!approveConfirm.item || !selectedBoqId) return;
		setApprovingId(approveConfirm.item.id);
		try {
			const result = await homeownerApproveBoqEntry(
				selectedBoqId,
				approveConfirm.item.boqSectionId,
				approveConfirm.item.id,
			);
			if (result.success) {
				setCats((prev) =>
					prev.map((c, cx) =>
						cx !== approveConfirm.ci
							? c
							: {
									...c,
									items: c.items.map((it, ix) =>
										ix !== approveConfirm.ii
											? it
											: { ...it, approved: true, approvedByHomeowner: true },
									),
								},
					),
				);
				toast.success("Entry approved successfully");
				void refreshStatus();
			} else {
				toast.error(result.error || "Failed to approve BOQ entry");
			}
		} catch (_err) {
			toast.error("Failed to approve BOQ entry");
		} finally {
			setApprovingId(null);
			setApproveConfirm({ show: false, ci: 0, ii: 0, item: null });
		}
	};

	const handleApproveAll = async () => {
		if (!selectedBoqId) return;
		const unapprovedEntries: Array<{ id: string; boqSectionId: string }> = [];
		for (const c of cats) {
			for (const it of c.items) {
				if (!it.approvedByHomeowner) {
					unapprovedEntries.push({ id: it.id, boqSectionId: it.boqSectionId });
				}
			}
		}
		if (unapprovedEntries.length === 0) return;

		setApprovingAll(true);
		try {
			const result = await homeownerBulkApproveBoqEntries(
				selectedBoqId,
				unapprovedEntries.map((e) => e.id),
			);
			if (result.success) {
				setCats((prev) =>
					prev.map((c) => ({
						...c,
						items: c.items.map((it) => ({
							...it,
							approved: true,
							approvedByHomeowner: true,
						})),
					})),
				);
				toast.success("All entries approved successfully");
				void refreshStatus();
			} else {
				toast.error(result.error || "Failed to approve all entries");
			}
		} catch (_err) {
			toast.error("Failed to approve all entries");
		} finally {
			setApprovingAll(false);
			setApproveAllConfirm(false);
		}
	};

	const unapprovedCount = cats.reduce(
		(n, c) => n + c.items.filter((it) => !it.approvedByHomeowner).length,
		0,
	);

	// Every BOQ the designer has issued: sent, approved (every line approved) or
	// locked. Their lines are final enough to show.
	const sentBoqs = boqs
		? boqs.filter(
				(boq) =>
					boq.status === "sent" ||
					boq.status === "approved" ||
					boq.status === "locked",
			)
		: [];

	// Drafts and change-requested BOQs are visible as "in preparation" so the
	// homeowner knows a quote is coming - without exposing unfinished line
	// items or a moving total.
	const preparingBoqs = boqs
		? boqs.filter(
				(boq) => boq.status === "draft" || boq.status === "change_requested",
			)
		: [];

	if (boqsLoading || loadingDetails) {
		return (
			<SkeletonRows label={t("loaders.loadingBoq")} className="min-h-75" />
		);
	}

	if (!boqs || boqs.length === 0) {
		return (
			<div>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-end",
						marginBottom: 16,
						flexWrap: "wrap",
						gap: 16,
					}}
				>
					<div>
						<Eyebrow>BILL OF QUANTITIES</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 30,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 0",
							}}
						>
							Every line,{" "}
							<ItalicAccent size={30} color={accent}>
								priced
							</ItalicAccent>
						</h2>
					</div>
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minHeight: 200,
						background: "var(--color-dashboard-surface)",
						borderRadius: 8,
					}}
				>
					<Eyebrow>No BOQ created yet</Eyebrow>
				</div>
			</div>
		);
	}

	// List view: Show BOQ cards grid (only sent BOQs)
	if (view === "list") {
		return (
			<div>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-end",
						marginBottom: 16,
						flexWrap: "wrap",
						gap: 16,
					}}
				>
					<div>
						<Eyebrow>BILL OF QUANTITIES</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 30,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 0",
							}}
						>
							Every line,{" "}
							<ItalicAccent size={30} color={accent}>
								priced
							</ItalicAccent>
						</h2>
					</div>
				</div>

				{sentBoqs.length === 0 && preparingBoqs.length === 0 ? (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 200,
							background: "var(--color-dashboard-surface)",
							borderRadius: 8,
						}}
					>
						<Eyebrow>No BOQ sent yet</Eyebrow>
					</div>
				) : (
					<>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
								gap: 16,
							}}
						>
							{sentBoqs.map((boq) => (
								<FloatPanel
									key={boq.id}
									elev={1}
									padding={0}
									style={{
										cursor: "pointer",
										overflow: "hidden",
										transition: "transform 160ms ease, box-shadow 160ms ease",
									}}
									onClick={() => handleSelectBoq(boq.id)}
								>
									<div
										style={{
											padding: "18px 20px",
											borderBottom: "1px solid var(--color-dashboard-border)",
										}}
									>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
												marginBottom: 8,
											}}
										>
											<span
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 9,
													fontWeight: 600,
													letterSpacing: "0.12em",
													color: "var(--color-dashboard-muted)",
													textTransform: "uppercase",
												}}
											>
												VERSION {boq.versionNumber}
											</span>
											<span
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 11,
													padding: "3px 8px",
													borderRadius: 3,
													background:
														boq.status === "sent"
															? "var(--color-dashboard-pill-awaiting-bg)"
															: boq.status === "approved"
																? "var(--color-dashboard-pill-done-bg)"
																: boq.status === "locked"
																	? "var(--color-dashboard-pill-info-bg)"
																	: "var(--color-dashboard-divider)",
													color:
														boq.status === "sent"
															? "var(--color-dashboard-warning)"
															: boq.status === "approved"
																? "var(--color-dashboard-success-strong)"
																: boq.status === "locked"
																	? "var(--color-dashboard-info-strong)"
																	: "var(--color-dashboard-ink-2)",
												}}
											>
												{boq.status.replace("_", " ")}
											</span>
										</div>
										<div
											style={{
												fontFamily: "var(--font-display)",
												fontWeight: 500,
												fontSize: 18,
												color: "var(--color-dashboard-text)",
											}}
										>
											{boq.title || `BOQ v${boq.versionNumber}`}
										</div>
									</div>
									<div style={{ padding: "16px 20px" }}>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												marginBottom: 6,
											}}
										>
											<span
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 12,
													color: "var(--color-dashboard-muted)",
												}}
											>
												Total
											</span>
											<span
												style={{
													fontFamily: "var(--font-display)",
													fontWeight: 500,
													fontSize: 15,
													color: "var(--color-dashboard-text)",
												}}
											>
												{formatPaiseINR(boq.grandTotal)}
											</span>
										</div>
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
											}}
										>
											<span
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 12,
													color: "var(--color-dashboard-muted)",
												}}
											>
												Sent
											</span>
											<span
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 11,
													color: "var(--color-dashboard-muted-dark)",
												}}
											>
												{new Date(boq.createdAt).toLocaleDateString("en-IN", {
													day: "numeric",
													month: "short",
													year: "numeric",
												})}
											</span>
										</div>
									</div>
									<div
										style={{
											padding: "12px 20px",
											background: "var(--color-dashboard-surface)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 6,
										}}
									>
										<Icn name="arrowRight" size={14} color={accent} />
										<span
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 12,
												fontWeight: 600,
												color: "var(--color-dashboard-accent-dark)",
											}}
										>
											View details
										</span>
									</div>
								</FloatPanel>
							))}
						</div>
						{preparingBoqs.length > 0 && (
							<div style={{ marginTop: 28 }}>
								<Eyebrow>IN PREPARATION · BY YOUR DESIGNER</Eyebrow>
								<div
									style={{
										display: "grid",
										gridTemplateColumns:
											"repeat(auto-fill, minmax(280px, 1fr))",
										gap: 16,
										marginTop: 12,
									}}
								>
									{preparingBoqs.map((boq) => (
										<FloatPanel
											key={boq.id}
											elev={1}
											padding={0}
											style={{ overflow: "hidden" }}
										>
											<div
												style={{
													padding: "18px 20px",
													borderBottom:
														"1px solid var(--color-dashboard-border)",
												}}
											>
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "flex-start",
														marginBottom: 8,
													}}
												>
													<span
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 9,
															fontWeight: 600,
															letterSpacing: "0.12em",
															color: "var(--color-dashboard-muted)",
															textTransform: "uppercase",
														}}
													>
														VERSION {boq.versionNumber}
													</span>
													<span
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 11,
															padding: "3px 8px",
															borderRadius: 3,
															background: "var(--color-dashboard-divider)",
															color: "var(--color-dashboard-ink-2)",
														}}
													>
														{boq.status === "change_requested"
															? "Changes requested"
															: "In preparation"}
													</span>
												</div>
												<div
													style={{
														fontFamily: "var(--font-display)",
														fontWeight: 500,
														fontSize: 18,
														color: "var(--color-dashboard-text)",
													}}
												>
													{boq.title || `BOQ v${boq.versionNumber}`}
												</div>
											</div>
											<div style={{ padding: "16px 20px" }}>
												<div
													style={{
														fontFamily: "var(--font-body)",
														fontSize: 12,
														color: "var(--color-dashboard-muted)",
														lineHeight: 1.5,
													}}
												>
													{boq.status === "change_requested"
														? "Your designer is updating this BOQ after your change request. You'll see the revised quote once it's re-sent."
														: "Your designer is preparing this quote. Every priced line appears here once it's sent to you."}
												</div>
											</div>
										</FloatPanel>
									))}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		);
	}

	// Detail view: Show BOQ items list with back button
	// This is the original items list rendering
	if (!boqDetails) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: 200,
					background: "var(--color-dashboard-surface)",
					borderRadius: 8,
				}}
			>
				<Eyebrow>Select a BOQ to view items</Eyebrow>
			</div>
		);
	}

	const boqGrand = cats.reduce(
		(s, c) => s + c.items.reduce((a, it) => a + it.amount, 0),
		0,
	);
	const boqApproved = cats.reduce(
		(s, c) =>
			s +
			c.items
				.filter((it) => it.approvedByHomeowner)
				.reduce((a, it) => a + it.amount, 0),
		0,
	);
	const boqPct = boqGrand ? Math.round((boqApproved / boqGrand) * 100) : 0;
	const boqUnapproved = cats.reduce(
		(n, c) => n + c.items.filter((it) => !it.approvedByHomeowner).length,
		0,
	);
	// Lines can be approved only on a Sent BOQ; an Approved one has none left
	// and a Locked one takes no more. Changes can be asked for until it is locked.
	const canApprove = boqDetails.boq.status === "sent";
	const canRequestChanges =
		boqDetails.boq.status === "sent" || boqDetails.boq.status === "approved";

	return (
		<div>
			{/* Header */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-end",
					marginBottom: 16,
					flexWrap: "wrap",
					gap: 16,
				}}
			>
				<div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
					<button
						type="button"
						onClick={handleBackToList}
						aria-label="Back to BOQ list"
						style={{
							width: 36,
							height: 36,
							borderRadius: 6,
							border: "1px solid var(--color-dashboard-border)",
							background: "var(--color-dashboard-paper)",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							marginTop: 8,
						}}
					>
						<Icn
							name="arrowLeft"
							size={16}
							color="var(--color-dashboard-muted-dark)"
						/>
					</button>
					<div>
						<Eyebrow>
							BILL OF QUANTITIES · v{boqDetails.boq.versionNumber}
						</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 30,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 0",
							}}
						>
							Every line,{" "}
							<ItalicAccent size={30} color={accent}>
								priced
							</ItalicAccent>
						</h2>
					</div>
				</div>
				<div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
					<StudioButton
						variant="secondary"
						size="md"
						onClick={() => openBoqPrintView(boqDetails.boq.id)}
						style={secondaryBtnStyle("md")}
					>
						<Icn name="download" size={16} />
						Print / Save as PDF
					</StudioButton>
					{canRequestChanges && (
						<StudioButton
							variant="secondary"
							size="md"
							onClick={() => setShowRequestChanges(true)}
							style={secondaryBtnStyle("md")}
						>
							<Icn name="message" size={16} />
							Request changes
						</StudioButton>
					)}
					{canApprove && boqUnapproved > 0 && (
						<Btn
							variant="primary"
							size="md"
							icon={
								<Icn
									name="checkSquare"
									size={16}
									color="var(--color-dashboard-on-fill)"
								/>
							}
							onClick={() => setApproveAllConfirm(true)}
						>
							Approve all lines
						</Btn>
					)}
				</div>
			</div>

			{showRequestChanges && (
				<RequestChangesDialog
					boq={boqDetails.boq}
					open={showRequestChanges}
					onOpenChange={setShowRequestChanges}
					onSuccess={() => {
						// The BOQ is now being revised, which the list shows under
						// "In preparation" without its lines.
						setShowRequestChanges(false);
						handleBackToList();
						void refetchBoqs();
					}}
				/>
			)}

			{/* Info banner + progress */}
			<div
				style={{
					display: "flex",
					gap: 10,
					padding: "13px 18px",
					background: "var(--color-dashboard-surface)",
					borderRadius: 8,
					marginBottom: 22,
					alignItems: "center",
					flexWrap: "wrap",
				}}
			>
				<Icn name="info" size={15} color={accent} style={{ flexShrink: 0 }} />
				<span
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 12.5,
						color: "var(--color-dashboard-muted-dark)",
						flex: 1,
						minWidth: 200,
					}}
				>
					This is the full itemised schedule{" "}
					{project?.designer?.name || "the designer"} prepared for your home.
					Approving a line says you agree to its quantity and price; if they
					change, it comes back to you. {boqPct}% of the value is approved.
				</span>
				<Progress value={boqPct} fill={accent} style={{ width: 160 }} />
			</div>

			{/* Categories */}
			{cats.map((c, ci) => {
				const catTotal = c.items.reduce((a, it) => a + it.amount, 0);
				return (
					<div key={c.id} style={{ marginBottom: 18 }}>
						{/* Category header */}
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "baseline",
								padding: "0 4px 10px",
							}}
						>
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 19,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
								}}
							>
								{c.name}
							</span>
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 17,
									color: "var(--color-dashboard-text)",
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{formatPaiseINR(catTotal)}
							</span>
						</div>
						<div
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 10.5,
								color: "var(--color-dashboard-muted)",
								textAlign: "right",
								padding: "0 4px 10px",
							}}
						>
							GST included: {formatGstIncluded(summariseGstIncluded(c.items))}
						</div>

						{/* Table */}
						<FloatPanel elev={1} padding={0} style={{ overflow: "hidden" }}>
							{/* Table head */}
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 12,
									padding: "11px 18px",
									background: "var(--color-dashboard-divider)",
									borderBottom: "1px solid var(--color-dashboard-border)",
								}}
							>
								<span style={{ ...boqHead, flex: 3 }}>Description</span>
								<span style={{ ...boqHead, flex: 1.4 }}>Room</span>
								<span style={{ ...boqHead, width: 64, textAlign: "right" }}>
									Qty
								</span>
								<span style={{ ...boqHead, width: 52 }}>Unit</span>
								<span style={{ ...boqHead, width: 92, textAlign: "right" }}>
									Rate
								</span>
								<span style={{ ...boqHead, width: 104, textAlign: "right" }}>
									Total
								</span>
								<span
									title={GST_INCLUDED_HINT}
									style={{ ...boqHead, width: 84, textAlign: "right" }}
								>
									GST incl.
								</span>
								<span style={{ ...boqHead, width: 78, textAlign: "center" }}>
									Approve
								</span>
							</div>

							{/* Rows */}
							{c.items.map((it, ii) => (
								<div
									key={it.id}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 12,
										padding: "12px 18px",
										borderBottom:
											ii < c.items.length - 1
												? "1px solid var(--color-dashboard-border-soft)"
												: 0,
										background: it.approved
											? "transparent"
											: "color-mix(in srgb, var(--color-dashboard-pill-awaiting) 5%, transparent)",
									}}
								>
									{/* Desc — clickable */}
									<button
										type="button"
										onClick={() => setBoqDetail({ ci, ii })}
										title="View product & similar options"
										style={{
											flex: 3,
											minWidth: 0,
											display: "flex",
											alignItems: "center",
											gap: 7,
											border: 0,
											background: "transparent",
											cursor: "pointer",
											textAlign: "left",
											padding: 0,
											fontFamily: "var(--font-body)",
											fontSize: 13,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
										}}
									>
										<span
											style={{
												minWidth: 0,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												borderBottom: `1.5px dotted ${accent}`,
											}}
										>
											{it.desc}
										</span>
										<Icn
											name="search"
											size={12}
											color={accent}
											style={{ flexShrink: 0 }}
										/>
									</button>

									<span
										style={{
											flex: 1.4,
											fontFamily: "var(--font-body)",
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
										}}
									>
										{it.room}
									</span>
									<span
										style={{
											width: 64,
											textAlign: "right",
											fontFamily: "var(--font-mono)",
											fontSize: 12,
											color: "var(--color-dashboard-text)",
										}}
									>
										{it.qty.toLocaleString()}
									</span>
									<span
										style={{
											width: 52,
											fontFamily: "var(--font-mono)",
											fontSize: 10.5,
											color: "var(--color-dashboard-muted)",
										}}
									>
										{it.unit}
									</span>
									<span
										style={{
											width: 92,
											textAlign: "right",
											fontFamily: "var(--font-mono)",
											fontSize: 12,
											color: "var(--color-dashboard-text)",
										}}
									>
										{formatPaiseINR(it.rate)}
									</span>
									<span
										style={{
											width: 104,
											textAlign: "right",
											fontFamily: "var(--font-ui)",
											fontSize: 13,
											fontWeight: 700,
											color: "var(--color-dashboard-text)",
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{formatPaiseINR(it.amount)}
									</span>
									<span
										style={{
											width: 84,
											textAlign: "right",
											fontFamily: "var(--font-mono)",
											fontSize: 11,
											color: "var(--color-dashboard-muted)",
										}}
									>
										{formatGstAmount(it.gstAmount)}
										<span style={{ display: "block", fontSize: 10 }}>
											{formatGstRate(it.gstRateBp)}
										</span>
									</span>

									{/* Checkbox */}
									<span
										style={{
											width: 78,
											display: "flex",
											justifyContent: "center",
										}}
									>
										<button
											type="button"
											onClick={() => toggleBoq(ci, ii, it)}
											title={
												it.approvedByHomeowner
													? "Approved by homeowner"
													: "Approve this line"
											}
											disabled={
												it.approvedByHomeowner ||
												!canApprove ||
												approvingId === it.id
											}
											style={{
												width: 22,
												height: 22,
												borderRadius: 4,
												border: 0,
												cursor:
													it.approvedByHomeowner || !canApprove
														? "default"
														: "pointer",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												background: it.approvedByHomeowner
													? "var(--color-dashboard-success-fill)"
													: approvingId === it.id
														? "var(--color-dashboard-accent)"
														: "var(--color-dashboard-paper)",
												boxShadow: it.approvedByHomeowner
													? "none"
													: "inset 0 0 0 1.5px var(--color-border-default)",
												transition: "all 160ms ease",
											}}
										>
											{approvingId === it.id ? (
												<Icn
													name="refreshCw"
													size={13}
													color="var(--color-dashboard-on-fill)"
												/>
											) : it.approvedByHomeowner ? (
												<Icn
													name="check"
													size={13}
													color="var(--color-dashboard-on-fill)"
												/>
											) : null}
										</button>
									</span>
								</div>
							))}
						</FloatPanel>
					</div>
				);
			})}

			{/* Grand total footer */}
			<FloatPanel
				elev={2}
				padding={0}
				style={{ overflow: "hidden", marginTop: 4 }}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "18px 24px",
						background: "var(--color-dashboard-dark)",
					}}
				>
					<div>
						<div
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 9,
								letterSpacing: "0.16em",
								color:
									"color-mix(in srgb, var(--color-dashboard-on-photo-warm) 70%, transparent)",
								textTransform: "uppercase",
							}}
						>
							Total project cost
						</div>
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12,
								color:
									"color-mix(in srgb, var(--color-dashboard-on-fill) 70%, transparent)",
								marginTop: 4,
							}}
						>
							{formatPaiseINR(boqApproved)} approved ·{" "}
							{formatPaiseINR(boqGrand - boqApproved)} awaiting your sign-off
						</div>
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12,
								color:
									"color-mix(in srgb, var(--color-dashboard-on-fill) 70%, transparent)",
								marginTop: 4,
							}}
						>
							GST included:{" "}
							{formatGstIncluded(
								summariseGstIncluded(cats.flatMap((cat) => cat.items)),
							)}
						</div>
					</div>
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 30,
							color: "var(--color-dashboard-on-fill)",
							letterSpacing: "-0.02em",
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{formatPaiseINR(boqGrand)}
					</div>
				</div>
			</FloatPanel>

			{/* Product detail modal */}
			{detailItem && (
				// biome-ignore lint/a11y/useKeyWithClickEvents: modal overlay
				// biome-ignore lint/a11y/noStaticElementInteractions: modal overlay
				<div
					onClick={() => setBoqDetail(null)}
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(30,41,59,0.5)",
						backdropFilter: "blur(4px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 100,
					}}
				>
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: stop propagation handler */}
					{/* biome-ignore lint/a11y/noStaticElementInteractions: stop propagation handler */}
					<div
						onClick={(e) => e.stopPropagation()}
						style={{
							width: 480,
							background: "var(--color-dashboard-paper)",
							borderRadius: 14,
							overflow: "hidden",
							boxShadow: "0 24px 80px rgba(30,41,59,0.25)",
						}}
					>
						{/* Modal header */}
						<div
							style={{
								padding: "20px 24px",
								borderBottom: "1px solid var(--color-dashboard-border)",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start",
							}}
						>
							<div>
								<Eyebrow style={{ fontSize: 9, marginBottom: 6 }}>
									BOQ LINE ITEM
								</Eyebrow>
								<h3
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 20,
										color: "var(--color-dashboard-text)",
									}}
								>
									{detailItem.desc}
								</h3>
							</div>
							<button
								type="button"
								onClick={() => setBoqDetail(null)}
								style={{
									width: 28,
									height: 28,
									borderRadius: 6,
									border: "none",
									background: "var(--color-dashboard-divider)",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Icn
									name="x"
									size={16}
									color="var(--color-dashboard-muted-dark)"
								/>
							</button>
						</div>

						{/* Modal body */}
						<div style={{ padding: "20px 24px" }}>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: 16,
									marginBottom: 20,
								}}
							>
								<div>
									<Eyebrow style={{ fontSize: 9, letterSpacing: "0.15em" }}>
										{" "}
										ROOM{" "}
									</Eyebrow>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-text)",
											marginTop: 4,
										}}
									>
										{detailItem.room}
									</p>
								</div>
								<div>
									<Eyebrow style={{ fontSize: 9, letterSpacing: "0.15em" }}>
										{" "}
										QUANTITY{" "}
									</Eyebrow>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-text)",
											marginTop: 4,
										}}
									>
										{detailItem.qty} {detailItem.unit}
									</p>
								</div>
								<div>
									<Eyebrow style={{ fontSize: 9, letterSpacing: "0.15em" }}>
										{" "}
										RATE{" "}
									</Eyebrow>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-text)",
											marginTop: 4,
										}}
									>
										{formatPaiseINR(detailItem.rate)} / {detailItem.unit}
									</p>
								</div>
								<div>
									<Eyebrow style={{ fontSize: 9, letterSpacing: "0.15em" }}>
										{" "}
										LINE TOTAL{" "}
									</Eyebrow>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-text)",
											marginTop: 4,
										}}
									>
										{formatPaiseINR(detailItem.amount)}
									</p>
								</div>
							</div>

							<div
								style={{
									background: "var(--color-dashboard-surface)",
									borderRadius: 7,
									padding: "14px 16px",
									display: "flex",
									justifyContent: "space-between",
									marginBottom: 16,
								}}
							>
								<span
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 13,
										color: "var(--color-dashboard-muted)",
									}}
								>
									Total amount
								</span>
								<span
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 20,
										color: "var(--color-dashboard-text)",
									}}
								>
									{formatPaiseINR(detailItem.amount)}
								</span>
							</div>

							{loadingAlternatives ? (
								<SkeletonRows
									label="Loading alternatives..."
									rows={2}
									className="p-4"
								/>
							) : alternatives ? (
								<div style={{ marginBottom: 16 }}>
									<Eyebrow
										style={{
											fontSize: 9,
											letterSpacing: "0.15em",
											marginBottom: 10,
										}}
									>
										ALTERNATIVES
									</Eyebrow>
									<div
										style={{ display: "flex", flexDirection: "column", gap: 8 }}
									>
										{alternatives.alternatives.value && (
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 10,
													padding: "10px 12px",
													background: "var(--color-dashboard-success-bg)",
													borderRadius: 6,
													border:
														"1px solid var(--color-dashboard-success-border)",
												}}
											>
												<div
													style={{
														width: 8,
														height: 8,
														borderRadius: "50%",
														background: "var(--color-dashboard-success-fill)",
													}}
												/>
												<div style={{ flex: 1 }}>
													<div
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 12,
															fontWeight: 600,
															color: "var(--color-dashboard-text)",
														}}
													>
														Value
													</div>
													<div
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 11,
															color: "var(--color-dashboard-muted-dark)",
														}}
													>
														{alternatives.alternatives.value.product.name}
													</div>
												</div>
												<div style={{ textAlign: "right" }}>
													<div
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 12,
															color: "var(--color-dashboard-pill-done)",
														}}
													>
														{
															alternatives.alternatives.value.product
																.priceDeltaPercent
														}
														% below
													</div>
													<div
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 11,
															color: "var(--color-dashboard-muted)",
														}}
													>
														{formatPaiseINR(
															alternatives.alternatives.value.product.price,
														)}
													</div>
												</div>
											</div>
										)}
										{alternatives.alternatives.premium && (
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 10,
													padding: "10px 12px",
													background: "var(--color-dashboard-surface)",
													borderRadius: 6,
													border: "1px solid var(--color-dashboard-border)",
												}}
											>
												<div
													style={{
														width: 8,
														height: 8,
														borderRadius: "50%",
														background: accent,
													}}
												/>
												<div style={{ flex: 1 }}>
													<div
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 12,
															fontWeight: 600,
															color: "var(--color-dashboard-text)",
														}}
													>
														Premium
													</div>
													<div
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 11,
															color: "var(--color-dashboard-muted-dark)",
														}}
													>
														{alternatives.alternatives.premium.product.name}
													</div>
												</div>
												<div style={{ textAlign: "right" }}>
													<div
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 12,
															color: "var(--color-dashboard-accent-dark)",
														}}
													>
														+
														{
															alternatives.alternatives.premium.product
																.priceDeltaPercent
														}
														% above
													</div>
													<div
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 11,
															color: "var(--color-dashboard-muted)",
														}}
													>
														{formatPaiseINR(
															alternatives.alternatives.premium.product.price,
														)}
													</div>
												</div>
											</div>
										)}
										{alternatives.alternatives.similar && (
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 10,
													padding: "10px 12px",
													background: "var(--color-dashboard-divider)",
													borderRadius: 6,
													border: "1px solid var(--color-border-default)",
												}}
											>
												<div
													style={{
														width: 8,
														height: 8,
														borderRadius: "50%",
														background: "var(--color-dashboard-muted)",
													}}
												/>
												<div style={{ flex: 1 }}>
													<div
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 12,
															fontWeight: 600,
															color: "var(--color-dashboard-text)",
														}}
													>
														Similar
													</div>
													<div
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 11,
															color: "var(--color-dashboard-muted-dark)",
														}}
													>
														{alternatives.alternatives.similar.product.name}
													</div>
												</div>
												<div style={{ textAlign: "right" }}>
													<div
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 12,
															color: "var(--color-dashboard-muted)",
														}}
													>
														~
														{
															alternatives.alternatives.similar.product
																.priceDeltaPercent
														}
														% diff
													</div>
													<div
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 11,
															color: "var(--color-dashboard-muted)",
														}}
													>
														{formatPaiseINR(
															alternatives.alternatives.similar.product.price,
														)}
													</div>
												</div>
											</div>
										)}
										{!alternatives.alternatives.value &&
											!alternatives.alternatives.premium &&
											!alternatives.alternatives.similar && (
												<div
													style={{
														padding: "12px",
														textAlign: "center",
														color: "var(--color-dashboard-muted)",
														fontSize: 12,
													}}
												>
													No alternatives available
												</div>
											)}
									</div>
								</div>
							) : null}

							<div
								style={{
									display: "flex",
									gap: 10,
									padding: "12px 14px",
									background: "var(--color-dashboard-surface)",
									borderRadius: 8,
									alignItems: "flex-start",
								}}
							>
								<Icn
									name="info"
									size={14}
									color={accent}
									style={{ marginTop: 1, flexShrink: 0 }}
								/>
								<span
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 11.5,
										lineHeight: 1.45,
										color: "var(--color-dashboard-muted-dark)",
									}}
								>
									Contact {project?.designer?.name || "the designer"} to discuss
									changes to scope, specification or quantity before approving.
								</span>
							</div>
						</div>

						{/* Modal footer */}
						<div
							style={{
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								display: "flex",
								gap: 8,
								justifyContent: "flex-end",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								onClick={() => setBoqDetail(null)}
								style={secondaryBtnStyle("sm")}
							>
								Close
							</StudioButton>
							<Btn
								variant="primary"
								size="sm"
								icon={<Icn name="message" size={13} />}
								onClick={() => {
									if (project?.designer?.userId && onAskDesigner) {
										onAskDesigner(project.designer.userId);
									}
								}}
							>
								Ask {project?.designer?.name || "the designer"}
							</Btn>
						</div>
					</div>
				</div>
			)}

			{/* Individual Approve Confirmation Modal */}
			{approveConfirm.show && approveConfirm.item && (
				<div
					role="dialog"
					aria-modal="true"
					onClick={(e) => {
						if (e.target === e.currentTarget && !approvingId) {
							setApproveConfirm({ show: false, ci: 0, ii: 0, item: null });
						}
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							setApproveConfirm({ show: false, ci: 0, ii: 0, item: null });
						}
					}}
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(30,41,59,0.5)",
						backdropFilter: "blur(4px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 100,
					}}
				>
					<div
						style={{
							width: 420,
							background: "var(--color-dashboard-paper)",
							borderRadius: 14,
							overflow: "hidden",
							boxShadow: "0 24px 80px rgba(30,41,59,0.25)",
						}}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<div
								style={{
									width: 48,
									height: 48,
									borderRadius: 8,
									background: "var(--color-dashboard-pill-awaiting-bg)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: 16,
								}}
							>
								<Icn
									name="info"
									size={24}
									color="var(--color-dashboard-pill-awaiting)"
								/>
							</div>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 20,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 8px 0",
								}}
							>
								Approve this line?
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								You're about to approve:{" "}
								<strong>{approveConfirm.item.desc}</strong>
							</p>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 13,
									color: "var(--color-dashboard-pill-awaiting)",
									lineHeight: 1.5,
									margin: "12px 0 0",
								}}
							>
								This action cannot be undone. Once approved, you cannot
								unapprove this line.
							</p>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								onClick={() =>
									setApproveConfirm({ show: false, ci: 0, ii: 0, item: null })
								}
								style={secondaryBtnStyle("sm")}
							>
								Cancel
							</StudioButton>
							<button
								type="button"
								onClick={handleApproveEntry}
								disabled={approvingId !== null}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									height: 36,
									paddingLeft: 14,
									paddingRight: 14,
									fontSize: 13,
									fontFamily: "var(--font-body)",
									fontWeight: 600,
									borderRadius: 8,
									border: "none",
									background: "var(--color-dashboard-success-fill)",
									color: "var(--color-dashboard-on-fill)",
									cursor: approvingId !== null ? "not-allowed" : "pointer",
									opacity: approvingId !== null ? 0.7 : 1,
								}}
							>
								{approvingId !== null ? "Approving..." : "Approve"}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Bulk Approve Confirmation Modal */}
			{approveAllConfirm && (
				<div
					role="dialog"
					aria-modal="true"
					onClick={(e) => {
						if (e.target === e.currentTarget && !approvingAll) {
							setApproveAllConfirm(false);
						}
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							setApproveAllConfirm(false);
						}
					}}
					style={{
						position: "fixed",
						inset: 0,
						background: "rgba(30,41,59,0.5)",
						backdropFilter: "blur(4px)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 100,
					}}
				>
					<div
						style={{
							width: 420,
							background: "var(--color-dashboard-paper)",
							borderRadius: 14,
							overflow: "hidden",
							boxShadow: "0 24px 80px rgba(30,41,59,0.25)",
						}}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<div
								style={{
									width: 48,
									height: 48,
									borderRadius: 8,
									background: "var(--color-dashboard-pill-awaiting-bg)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									marginBottom: 16,
								}}
							>
								<Icn
									name="info"
									size={24}
									color="var(--color-dashboard-pill-awaiting)"
								/>
							</div>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 20,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 8px 0",
								}}
							>
								Approve all {unapprovedCount} lines?
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								You're about to approve all {unapprovedCount} unapproved items
								in this BOQ.
							</p>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 13,
									color: "var(--color-dashboard-pill-awaiting)",
									lineHeight: 1.5,
									margin: "12px 0 0",
								}}
							>
								This action cannot be undone. Once approved, you cannot
								unapprove these lines.
							</p>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								onClick={() => setApproveAllConfirm(false)}
								style={secondaryBtnStyle("sm")}
							>
								Cancel
							</StudioButton>
							<button
								type="button"
								onClick={handleApproveAll}
								disabled={approvingAll}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									height: 36,
									paddingLeft: 14,
									paddingRight: 14,
									fontSize: 13,
									fontFamily: "var(--font-body)",
									fontWeight: 600,
									borderRadius: 8,
									border: "none",
									background: "var(--color-dashboard-success-fill)",
									color: "var(--color-dashboard-on-fill)",
									cursor: approvingAll ? "not-allowed" : "pointer",
									opacity: approvingAll ? 0.7 : 1,
								}}
							>
								{approvingAll
									? "Approving..."
									: `Approve all ${unapprovedCount} lines`}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
